import { useState } from 'react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Camera, Shield, Search, Users, Database, Eye, Clock } from 'lucide-react';
import Hero from '@/components/Hero';
import Features from '@/components/Features';
import Dashboard from '@/components/Dashboard';
import LoginModal from '@/components/LoginModal';
import Navigation from '@/components/Navigation';
import { Link } from "react-router-dom";

const Index = () => {
  const [showLogin, setShowLogin] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [loginTab, setLoginTab] = useState('login');

  const handleNavigate = (section: string) => {
    if (!isLoggedIn) {
      setShowLogin(true);
      return;
    }
  };

  if (isLoggedIn) {
    return <Dashboard />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-protec-dark via-protec-dark-light to-protec-dark-lighter text-white">
      {/* Header */}
      <header className="border-b border-protec-gold/20 bg-black/20 backdrop-blur-md sticky top-0 z-50">
        <div className="container mx-auto px-3 sm:px-4 py-3 sm:py-4 flex items-center justify-between">
          <div className="flex items-center space-x-2 sm:space-x-3">
            <img 
              src="/logo.png" 
              alt="Protec Bens" 
              className="h-10 w-10 sm:h-12 sm:w-12"
            />
          </div>
          
          <div className="flex items-center space-x-2 sm:space-x-4">
            <Link
              to="/"
              state={{ openRegister: true }}
              className="bg-gradient-gold text-black font-semibold hover:shadow-gold px-3 sm:px-4 py-2 rounded text-sm sm:text-base"
            >
              <span className="hidden xs:inline">Assinar Agora</span>
              <span className="xs:hidden">Assinar</span>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <Hero onGetStarted={() => setShowLogin(true)} />

      {/* Features Section */}
      <Features />

      {/* Stats Section */}
      <section className="py-12 sm:py-20 bg-black/30">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-8">
            <div className="text-center">
              <div className="text-2xl sm:text-4xl font-bold gradient-text mb-1 sm:mb-2">10,000+</div>
              <div className="text-gray-300 text-xs sm:text-base">Câmeras Ativas</div>
            </div>
            <div className="text-center">
              <div className="text-2xl sm:text-4xl font-bold gradient-text mb-1 sm:mb-2">24/7</div>
              <div className="text-gray-300 text-xs sm:text-base">Monitoramento</div>
            </div>
            <div className="text-center">
              <div className="text-2xl sm:text-4xl font-bold gradient-text mb-1 sm:mb-2">99.9%</div>
              <div className="text-gray-300 text-xs sm:text-base">Disponibilidade</div>
            </div>
            <div className="text-center">
              <div className="text-2xl sm:text-4xl font-bold gradient-text mb-1 sm:mb-2">5,000+</div>
              <div className="text-gray-300 text-xs sm:text-base">Clientes Ativos</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 sm:py-20 bg-gradient-to-r from-protec-gold/10 to-protec-gold-light/10">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-2xl sm:text-4xl font-bold mb-4 sm:mb-6 gradient-text">
            Pronto para começar?
          </h2>
          <p className="text-base sm:text-xl text-gray-300 mb-6 sm:mb-8 max-w-2xl mx-auto">
            Junte-se a milhares de profissionais que confiam na Protec Bens para monitoramento e investigação.
          </p>
          <Link
            to="/"
            state={{ openRegister: true }}
            className="inline-block bg-gradient-gold text-black font-bold px-4 py-2 text-base sm:text-lg hover:shadow-gold animate-pulse-gold rounded text-center"
          >
            Começar Agora
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black/50 border-t border-protec-gold/20 py-8 sm:py-12">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 sm:gap-8">
            <div className="col-span-2 md:col-span-1">
              <div className="flex items-center space-x-2 mb-4">
                <span className="font-bold gradient-text text-sm sm:text-base">PROTEC BENS</span>
              </div>
              <p className="text-gray-400 text-xs sm:text-sm">
                Líder em soluções de monitoramento e segurança nacional.
              </p>
            </div>
            <div>
              <h4 className="font-semibold text-protec-gold mb-3 sm:mb-4 text-sm sm:text-base">Soluções</h4>
              <ul className="text-gray-400 text-xs sm:text-sm space-y-1 sm:space-y-2">
                <li>Rastreamento de Veículos</li>
                <li>Reconhecimento Facial</li>
                <li>Câmeras ao Vivo</li>
                <li>Relatórios</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-protec-gold mb-3 sm:mb-4 text-sm sm:text-base">Empresa</h4>
              <ul className="text-gray-400 text-xs sm:text-sm space-y-1 sm:space-y-2">
                <li>Sobre Nós</li>
                <li>Contato</li>
                <li>Suporte</li>
                <li>Blog</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-protec-gold mb-3 sm:mb-4 text-sm sm:text-base">Legal</h4>
              <ul className="text-gray-400 text-xs sm:text-sm space-y-1 sm:space-y-2">
                <li>Privacidade</li>
                <li>Termos de Uso</li>
                <li>LGPD</li>
                <li>Segurança</li>
              </ul>
            </div>
          </div>
          <div className="border-t border-protec-gold/20 mt-6 sm:mt-8 pt-6 sm:pt-8 text-center text-gray-400 text-xs sm:text-sm">
            © 2025 Protec Bens. Todos os direitos reservados.
          </div>
        </div>
      </footer>

      {/* Login Modal */}
      <LoginModal 
        open={showLogin} 
        onOpenChange={setShowLogin}
        onLoginSuccess={() => {
          setIsLoggedIn(true);
          setShowLogin(false);
        }}
        defaultTab={loginTab}
      />
    </div>
  );
};

export default Index;
